import React, { useMemo, useState } from "react";

import {
  formatFractionDisplay,
  queryIsLoading,
  useGetFootprintingPigletFeedInformationByPrimaryDataId,
  useGetFootprintingPigletFeedProductions,
  useGetFootprintingPigletHerdDetails,
  useGetFootprintingPigletManureAdditive,
  useGetFootprintingPigletManureStorage,
  useGetManureAdditives,
  useGetPigletFeedTypes,
  useGetPigletManureStorage,
  useTranslation,
} from "utils";

import { FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE, PigletFootprintReturnBody } from "models";

import { Card, ListCard } from "components";

import { FootprintingPigletFeedProductionDrawer } from "pages/app/footprinting/components/FootprintingPigletFeedProductionDrawer";
import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";
import { PigletFeedInformationForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletFeedInformationForm";
import { PigletHerdDetailsForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletHerdDetailsForm";
import { PigletManureAdditiveForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletManureAdditiveForm";
import { PigletManureStorageForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletManureStorageForm";
import { PigletMoorlandForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletMoorlandForm";
import { PigletStableTypeForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletStableTypeForm";
import { PigletYieldForm } from "pages/app/footprinting/components/formFlows/pigletFlow/PigletYieldForm";
import { PigletCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

interface FootprintEditPigletDetailsCardProps {
  pigletFootprintData: PigletFootprintReturnBody;
  isPigletFootprintDataLoading: boolean;
  setShowEditModal: (show: boolean) => void;
  setPigletModalForm: (formType: string) => void;
  farmId: string;
  year: number;
}

const FootprintEditPigletDetailsCard = ({
  pigletFootprintData,
  isPigletFootprintDataLoading,
  setShowEditModal,
  setPigletModalForm,
  farmId,
  year,
}: FootprintEditPigletDetailsCardProps) => {
  const { t } = useTranslation(["footprintingPiglet", "footprinting", "piglet"]);

  const feedTypesQuery = useGetPigletFeedTypes();
  const manureStorageQuery = useGetPigletManureStorage();
  const manureAdditivesQuery = useGetManureAdditives();

  const { footprint } = pigletFootprintData;

  const herdDetailsQuery = useGetFootprintingPigletHerdDetails(footprint?.id ?? "", { enabled: !!footprint?.id });
  const feedInformationQuery = useGetFootprintingPigletFeedInformationByPrimaryDataId(footprint?.id ?? "");
  const manureStorageUsageQuery = useGetFootprintingPigletManureStorage(footprint?.id);
  const manureAdditiveUsageQuery = useGetFootprintingPigletManureAdditive(footprint?.id);

  const selfProducedFeeds = useMemo(() => (feedInformationQuery.data ?? []).filter((feedInfo) => feedInfo.isSelfProduced), [feedInformationQuery.data]);
  const feedProductionQueries = useGetFootprintingPigletFeedProductions(selfProducedFeeds.map((feedInfo) => feedInfo.id ?? ""));

  const [selectedFeedInformationId, setSelectedFeedInformationId] = useState<string | null>(null);

  // Verified footprints are read-only: the admin unverify is the only write the API accepts
  const editButton = (formKey: string) =>
    footprint?.isVerified
      ? undefined
      : {
          label: t("footprinting:review.edit"),
          onClick: () => {
            setShowEditModal(true);
            setPigletModalForm(formKey);
          },
        };

  const displayRadioAnswer = (isActive: boolean | null) => (isActive === null ? "" : t(`footprinting:radioAnswer.${isActive}`, { ns: "footprinting" }));
  const isLoading =
    isPigletFootprintDataLoading ||
    [feedTypesQuery, manureStorageQuery, manureAdditivesQuery, herdDetailsQuery, feedInformationQuery, manureStorageUsageQuery, manureAdditiveUsageQuery].some(
      queryIsLoading
    ) ||
    feedProductionQueries.some(queryIsLoading);

  return (
    <Card className="admin-edit-piglet-details-card padding-125">
      <ReviewPageTitle title={t("review.piglet.title", { ns: "footprinting" })} iconName="tabler/pig" isLoading={isLoading} />
      {/* Yield */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.pigletYield.cardTitle")}
        titleButton={editButton(PigletYieldForm.Key)}
        dataPairs={[
          {
            label: t("pigletYieldForm.question.averageSalesWeightLabel"),
            value: `${footprint?.averageSalesWeightKg ?? ""} ${t("pigletYieldForm.question.averageSalesWeightSuffix")}`,
          },
          { label: t("pigletYieldForm.question.averageNumberOfLittersLabel"), value: `${footprint?.averageNumberOfLitters ?? ""}` },
          {
            label: t("pigletYieldForm.question.averageLitterSizeLabel"),
            value: `${footprint?.averageLitterSize ?? ""} ${t("pigletYieldForm.question.averageLitterSizeSuffix")}`,
          },
        ]}
      />
      {/* Herd details */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.herdDetails.cardTitle")}
        titleButton={editButton(PigletHerdDetailsForm.Key)}
        dataPairs={[
          {
            label: t("pigletHerdDetailsForm.question.averageNumberOfSowsInput"),
            value: `${herdDetailsQuery.data?.averageNumberOfSows ?? ""} ${t("pigletHerdDetailsForm.question.averageNumberOfSowsSuffix")}`,
          },
          {
            label: t("pigletHerdDetailsForm.question.yearlyReplacementRateInput"),
            value: `${formatFractionDisplay(herdDetailsQuery.data?.yearlyReplacementRatePct ?? 0)} %`,
          },
          {
            label: t("pigletHerdDetailsForm.question.sucklingPigletLossRateInput"),
            value: `${formatFractionDisplay(herdDetailsQuery.data?.sucklingPigletLossRatePct ?? 0)} %`,
          },
          {
            label: t("pigletHerdDetailsForm.question.rearingLossInput"),
            value: `${formatFractionDisplay(herdDetailsQuery.data?.rearingLossPct ?? 0)} %`,
          },
          {
            label: t("pigletHerdDetailsForm.question.soldSucklingPigsInput"),
            value: `${formatFractionDisplay(herdDetailsQuery.data?.soldSucklingPigsPct ?? 0)} %`,
          },
        ]}
      />
      {/* Stable type — one card per stable, all edited via the same shared multi-entry form */}
      {(footprint?.stables ?? []).map((stable, index) => (
        <ListCard
          key={stable.id}
          className="margin-bottom-100"
          isOutlined
          isLoading={isLoading}
          title={t("pigletStableTypeForm.stableNumber", { count: index + 1 })}
          titleButton={editButton(PigletStableTypeForm.Key)}
          dataPairs={[
            { label: t("pigletStableTypeForm.question.stableTypeLabel"), value: t(`pigletStableTypeForm.stableTypeOptions.${stable.stableType}`) },
            {
              label: t("pigletStableTypeForm.question.stableSizeLabel"),
              value: `${stable.stableSize ?? ""} ${t("pigletStableTypeForm.question.stableSizeSuffix")}`,
            },
            { label: t("pigletStableTypeForm.question.ammoniaFiltrationQuestion"), value: displayRadioAnswer(stable.hasAmmoniaFiltration) },
            ...(stable.hasAmmoniaFiltration
              ? [
                  {
                    label: t("pigletStableTypeForm.question.ammoniaEfficiencyLabel"),
                    value: t(`pigletStableTypeForm.ammoniaEfficiencyOptions.${stable.ammoniaFiltrationEfficiency}`),
                  },
                ]
              : []),
          ]}
        />
      ))}
      {/* Feed types / amounts */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.feedInformation.cardTitle")}
        titleButton={editButton(PigletFeedInformationForm.Key)}
        dataPairs={(feedInformationQuery.data ?? []).flatMap((feedInfo) => {
          const feedTypeName = feedTypesQuery.data?.find(({ id }) => id === feedInfo.feedId)?.name ?? "";
          return [
            { label: feedTypeName, value: `${feedInfo.energyValue ?? ""} ${t("reviewAndSubmit.feedInformation.energyValueUnit")}` },
            {
              label: `${feedTypeName} - ${t("pigletFeedInformationForm.question.pigletsPct")}`,
              value: `${formatFractionDisplay(feedInfo.pigletsPct ?? 0)} %`,
            },
            {
              label: `${feedTypeName} - ${t("pigletFeedInformationForm.question.sowsPct")}`,
              value: `${formatFractionDisplay(feedInfo.sowsPct ?? 0)} %`,
            },
          ];
        })}
      />
      {/* Self-produced feed production */}
      {selfProducedFeeds.map((feedInfo, index) => {
        const feedTypeName = feedTypesQuery.data?.find(({ id }) => id === feedInfo.feedId)?.name ?? "";
        const production = feedProductionQueries[index]?.data;
        const isSplitRelevant = production?.cropUsedForFeed === FARM_FOOTPRINTING_DAIRY_FEED_PRODUCTION_CROP_USAGE.NO;

        return (
          <ListCard
            key={feedInfo.id}
            className="margin-bottom-100"
            isOutlined
            isLoading={isLoading}
            title={t("reviewAndSubmit.feedInformation.selfProducedTitle", { feedTypeName: feedTypeName || t("pigletFeedProductionForm.title") })}
            titleButton={
              footprint?.isVerified ? undefined : { label: t("footprinting:review.edit"), onClick: () => setSelectedFeedInformationId(feedInfo.id ?? null) }
            }
            dataPairs={[
              {
                label: t("pigletFeedProductionForm.question.cropUsageRadioTitle"),
                value: production?.cropUsedForFeed ? t(`pigletFeedProductionForm.cropUsageOptions.${production.cropUsedForFeed}`) : "",
              },
              { label: t("pigletFeedProductionForm.question.cropYieldTPerHa"), value: `${production?.cropYieldTPerHa ?? ""}` },
              { label: t("pigletFeedProductionForm.question.dieselConsumptionLHa"), value: `${production?.dieselConsumptionLHa ?? ""}` },
              { label: t("pigletFeedProductionForm.question.energyUsedKwhHa"), value: `${production?.energyUsedKwhHa ?? ""}` },
              { label: t("pigletFeedProductionForm.question.mineralNitrogenKgHa"), value: `${production?.mineralNitrogenKgHa ?? ""}` },
              { label: t("pigletFeedProductionForm.question.mineralPhosphorusKgHa"), value: `${production?.mineralPhosphorusKgHa ?? ""}` },
              { label: t("pigletFeedProductionForm.question.mineralPotassiumKgHa"), value: `${production?.mineralPotassiumKgHa ?? ""}` },
              { label: t("pigletFeedProductionForm.question.nitrogenOrganicFertKgHa"), value: `${production?.nitrogenOrganicFertKgHa ?? ""}` },
              { label: t("pigletFeedProductionForm.question.totalLimeAppliedKgCaCo3PerHa"), value: `${production?.totalLimeAppliedKgCaCo3PerHa ?? ""}` },
              {
                label: t("pigletFeedProductionForm.question.totalPlantProtectionApplied"),
                value: `${production?.totalPlantProtectionApplied ?? ""} ${production?.totalPlantProtectionAppliedUnit ?? ""}`.trim(),
              },
              {
                label: t("pigletFeedProductionForm.question.totalWaterUsedIrregationLPerHa"),
                value: `${production?.totalWaterUsedIrregationLPerHa ?? ""}`,
              },
              ...(isSplitRelevant
                ? [
                    { label: t("pigletFeedProductionForm.question.soldOffFarmPct"), value: `${formatFractionDisplay(production?.soldOffFarmPct ?? 0)} %` },
                    {
                      label: t("pigletFeedProductionForm.question.fedToPigletsPct"),
                      value: `${formatFractionDisplay(production?.fedToPigletsPct ?? 0)} %`,
                    },
                    { label: t("pigletFeedProductionForm.question.otherUsesPct"), value: `${formatFractionDisplay(production?.otherUsesPct ?? 0)} %` },
                  ]
                : []),
            ]}
          />
        );
      })}
      {/* Manure storage */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.manureStorage.cardTitle")}
        titleButton={editButton(PigletManureStorageForm.Key)}
        dataPairs={(manureStorageUsageQuery.data ?? []).map((manureStorageUsage) => ({
          label: manureStorageQuery.data?.find(({ id }) => id === manureStorageUsage.manureStorageId)?.name ?? "Unknown",
          value: `${formatFractionDisplay(manureStorageUsage.amountStoredPct)} %`,
        }))}
      />
      {/* Manure additives */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.manureAdditives.cardTitle")}
        titleButton={editButton(PigletManureAdditiveForm.Key)}
        dataPairs={[
          {
            label: t("reviewAndSubmit.manureAdditives.sluryAcidifiersUsed"),
            value: displayRadioAnswer(footprint?.manureAdditiveActive === "yes"),
          },
          ...(manureAdditiveUsageQuery.data ?? []).map((manureAdditiveUsage) => ({
            label: manureAdditivesQuery.data?.find(({ id }) => manureAdditiveUsage.manureAdditiveId === id)?.name ?? "",
            value: `${manureAdditiveUsage.amountUsedM3}`,
          })),
        ]}
      />
      {/* Moorland */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("reviewAndSubmit.moorland.cardTitle")}
        titleButton={editButton(PigletMoorlandForm.Key)}
        dataPairs={[
          {
            label: t("reviewAndSubmit.moorland.moorlandUsed"),
            value: displayRadioAnswer(footprint?.moorlandActive === "yes"),
          },
          ...(footprint?.moorlandActive === "yes"
            ? [
                {
                  label: t("reviewAndSubmit.moorland.fodderArea"),
                  value: `${footprint.moorlandAreaForFodderProduction} ${t("pigletMoorlandForm.question.fodderProductionAreaSuffix")}`,
                },
                {
                  label: t("reviewAndSubmit.moorland.averageWaterLevel"),
                  value: `${footprint.moorlandAnnualWaterLevel} ${t("pigletMoorlandForm.question.annualWaterLevelSuffix")}`,
                },
              ]
            : []),
        ]}
      />
      {/* Comment */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isLoading}
        title={t("commentForm.title", { ns: "footprinting" })}
        titleButton={editButton(PigletCommentForm.Key)}
        dataPairs={[
          {
            label: t("commentForm.label", { ns: "footprinting" }),
            value: footprint?.comment || t("commentForm.noComment", { ns: "footprinting" }),
          },
        ]}
      />
      {selectedFeedInformationId && (
        <FootprintingPigletFeedProductionDrawer
          farmId={farmId}
          year={year}
          visible={selectedFeedInformationId !== null}
          onClose={() => setSelectedFeedInformationId(null)}
          feedInformationId={selectedFeedInformationId}
          primaryDataId={footprint?.id}
          feedTypeName={
            feedTypesQuery.data?.find(({ id }) => id === selfProducedFeeds.find((feedInfo) => feedInfo.id === selectedFeedInformationId)?.feedId)?.name
          }
        />
      )}
    </Card>
  );
};

export default FootprintEditPigletDetailsCard;
